/**
 * Glyphs — Phaser-tegnet erstatning for emoji-glyphs i UI.
 *
 * Bygd i runde 45 etter persona-tilbakemelding (Karin/UX-designer) som
 * flagget emoji-rester i historikk/karriere-modaler. Vi har allerede
 * dedikerte WebP-ikoner for FAB-er og etasjer; her er enkle vektor-
 * glyphs til steder hvor et helt asset ville være overkill (en kort
 * status-tekst som "Brage-vinner" trenger bare ett dekorativt symbol).
 *
 * Hver funksjon returnerer en Phaser.Graphics-objekt sentrert på (x, y)
 * med farge satt via parameter. Bredde/høyde dokumenteres slik at
 * kalleren kan reservere plass.
 */
import Phaser from 'phaser';

/**
 * Stilisert pokal / trofé. Total bredde ~16px, høyde ~18px,
 * sentrert horisontalt på (x, y) (med y som baseline-ish).
 */
export function tegnTroféGlyph(
  scene: Phaser.Scene,
  x: number,
  y: number,
  farge: number,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  g.fillStyle(farge, 1);
  // Bolle (rektangel med litt buet bunn)
  g.fillRoundedRect(-6, -10, 12, 10, { tl: 2, tr: 2, bl: 0, br: 0 });
  // Ører på siden av bollen
  g.lineStyle(2, farge, 1);
  g.beginPath();
  g.arc(-8, -6, 3, Math.PI / 2, -Math.PI / 2, true);
  g.strokePath();
  g.beginPath();
  g.arc(8, -6, 3, -Math.PI / 2, Math.PI / 2, false);
  g.strokePath();
  // Stilk
  g.fillRect(-2, 0, 4, 4);
  // Fot
  g.fillRect(-6, 4, 12, 3);
  return g;
}

/**
 * Stilisert 5-tagget stjerne. Total bredde ~14px, høyde ~14px,
 * sentrert på (x, y).
 */
export function tegnStjerneGlyph(
  scene: Phaser.Scene,
  x: number,
  y: number,
  farge: number,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  g.fillStyle(farge, 1);
  const ytre = 7;
  const indre = 3;
  const punkter: Array<[number, number]> = [];
  for (let i = 0; i < 10; i += 1) {
    const r = i % 2 === 0 ? ytre : indre;
    const v = (Math.PI * i) / 5 - Math.PI / 2;
    punkter.push([Math.cos(v) * r, Math.sin(v) * r]);
  }
  g.beginPath();
  g.moveTo(punkter[0][0], punkter[0][1]);
  for (let i = 1; i < punkter.length; i += 1) {
    g.lineTo(punkter[i][0], punkter[i][1]);
  }
  g.closePath();
  g.fillPath();
  return g;
}

/**
 * Stilisert bok-stabel (3 horisontale rektangler oppå hverandre).
 * Total bredde ~20px, høyde ~16px, sentrert på (x, y).
 */
export function tegnBokstabelGlyph(
  scene: Phaser.Scene,
  x: number,
  y: number,
  farge: number,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  g.fillStyle(farge, 1);
  // Bunn-bok (bredest)
  g.fillRoundedRect(-10, 2, 20, 5, 1);
  // Midtre bok (litt smalere, forskjøvet)
  g.fillRoundedRect(-8, -4, 16, 5, 1);
  // Topp-bok (smalest)
  g.fillRoundedRect(-7, -10, 14, 5, 1);
  return g;
}

/**
 * Stilisert penn (skrå strek + spiss). Total bredde ~16px, høyde ~16px,
 * sentrert på (x, y).
 */
export function tegnPennGlyph(
  scene: Phaser.Scene,
  x: number,
  y: number,
  farge: number,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  g.fillStyle(farge, 1);
  // Pennens skaft som en skrå rektangel
  g.beginPath();
  g.moveTo(-6, 6);
  g.lineTo(-4, 8);
  g.lineTo(6, -2);
  g.lineTo(4, -4);
  g.closePath();
  g.fillPath();
  // Spiss
  g.beginPath();
  g.moveTo(-6, 6);
  g.lineTo(-8, 8);
  g.lineTo(-4, 8);
  g.closePath();
  g.fillPath();
  // Toppdetalj (radérgummi-aktig)
  g.beginPath();
  g.moveTo(6, -2);
  g.lineTo(7, -3);
  g.lineTo(5, -5);
  g.lineTo(4, -4);
  g.closePath();
  g.fillPath();
  return g;
}

/**
 * Stilisert konvolutt (rektangel med diagonal "klaff" på toppen).
 * Total bredde ~16px, høyde ~12px, sentrert på (x, y).
 */
export function tegnKonvoluttGlyph(
  scene: Phaser.Scene,
  x: number,
  y: number,
  farge: number,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  g.lineStyle(2, farge, 1);
  g.strokeRect(-8, -6, 16, 12);
  g.beginPath();
  g.moveTo(-8, -6);
  g.lineTo(0, 1);
  g.lineTo(8, -6);
  g.strokePath();
  return g;
}

/**
 * Stilisert hatt / gradus-cap. Total bredde ~16px, høyde ~12px,
 * sentrert på (x, y).
 */
export function tegnGradusGlyph(
  scene: Phaser.Scene,
  x: number,
  y: number,
  farge: number,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  g.fillStyle(farge, 1);
  // Topp (firkant rotert 45°)
  g.beginPath();
  g.moveTo(0, -6);
  g.lineTo(8, -2);
  g.lineTo(0, 2);
  g.lineTo(-8, -2);
  g.closePath();
  g.fillPath();
  // Tøyserre (stripe ned)
  g.fillRect(4, -2, 2, 6);
  g.fillCircle(5, 5, 1.5);
  return g;
}

/**
 * Stilisert hjerte (lojalitets-glyph). Total bredde ~14px, høyde ~12px,
 * sentrert på (x, y).
 */
export function tegnHjerteGlyph(
  scene: Phaser.Scene,
  x: number,
  y: number,
  farge: number,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  g.fillStyle(farge, 1);
  g.fillCircle(-3, -2, 4);
  g.fillCircle(3, -2, 4);
  g.beginPath();
  g.moveTo(-7, -1);
  g.lineTo(0, 7);
  g.lineTo(7, -1);
  g.closePath();
  g.fillPath();
  return g;
}

/**
 * Stilisert dokument / kontrakt (rektangel med "tekst-streker" inni).
 * Total bredde ~14px, høyde ~16px, sentrert på (x, y).
 */
export function tegnDokumentGlyph(
  scene: Phaser.Scene,
  x: number,
  y: number,
  farge: number,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  g.lineStyle(1.5, farge, 1);
  g.strokeRect(-6, -8, 12, 16);
  g.lineStyle(1, farge, 0.85);
  g.beginPath();
  g.moveTo(-3, -4);
  g.lineTo(3, -4);
  g.moveTo(-3, -1);
  g.lineTo(3, -1);
  g.moveTo(-3, 2);
  g.lineTo(3, 2);
  g.strokePath();
  return g;
}

/**
 * Stilisert sølv-medalje (sirkel med diagonal stripe). Total bredde ~14px.
 */
export function tegnMedaljeGlyph(
  scene: Phaser.Scene,
  x: number,
  y: number,
  farge: number,
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  g.fillStyle(farge, 1);
  // Sløyfe (to trekanter)
  g.beginPath();
  g.moveTo(-5, -8);
  g.lineTo(0, -3);
  g.lineTo(-2, -8);
  g.closePath();
  g.fillPath();
  g.beginPath();
  g.moveTo(5, -8);
  g.lineTo(0, -3);
  g.lineTo(2, -8);
  g.closePath();
  g.fillPath();
  // Medaljen
  g.fillCircle(0, 3, 5);
  g.lineStyle(1.5, 0xffffff, 0.6);
  g.strokeCircle(0, 3, 3);
  return g;
}

/**
 * Felles type for karriere-glyph (brukes som ikon i ForfatterKarriereModal).
 */
export type KarriereGlyphType =
  | 'kontrakt'
  | 'bok'
  | 'stjerne'
  | 'gradus'
  | 'medalje'
  | 'trofé'
  | 'hjerte';

export function tegnKarriereGlyph(
  scene: Phaser.Scene,
  type: KarriereGlyphType,
  x: number,
  y: number,
  farge: number,
): Phaser.GameObjects.Graphics {
  switch (type) {
    case 'kontrakt':
      return tegnDokumentGlyph(scene, x, y, farge);
    case 'bok':
      return tegnBokstabelGlyph(scene, x, y, farge);
    case 'stjerne':
      return tegnStjerneGlyph(scene, x, y, farge);
    case 'gradus':
      return tegnGradusGlyph(scene, x, y, farge);
    case 'medalje':
      return tegnMedaljeGlyph(scene, x, y, farge);
    case 'trofé':
      return tegnTroféGlyph(scene, x, y, farge);
    case 'hjerte':
      return tegnHjerteGlyph(scene, x, y, farge);
  }
}
